import { createContext, ReactNode, useContext } from "react";
import { Experiment } from "./experiments";

export type FeatureFlag =
  | "CUSTOMER_CLIENT_ALLOW_SIGNUPS"
  | "CUSTOMER_CLIENT_MATCHED_RENEWAL_LANGUAGE"
  | "CUSTOMER_CLIENT_ADDRESS_FIRST_ENROLLMENT"
  | "CUSTOMER_CLIENT_CREDIT_FOCUSED_RATE";

type FeatureFlagContextType = {
  features: FeatureFlag[];
};

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
  features: [],
});

type ProviderProps = {
  features: string[];
  children: ReactNode;
};

function Provider({ features, children }: ProviderProps) {
  return (
    <FeatureFlagContext.Provider
      value={{ features: features as FeatureFlag[] }}
    >
      <Experiment.Provider featureFlags={features as FeatureFlag[]}>
        {children}
      </Experiment.Provider>
    </FeatureFlagContext.Provider>
  );
}

/** Whether the given feature flag is enabled for this user. */
export function useFeature(feature: FeatureFlag): boolean {
  const { features } = useContext(FeatureFlagContext);
  return features.includes(feature);
}

type GateProps = {
  feature: FeatureFlag;
  children: ReactNode;
};

/** Renders its children only when the given feature flag is enabled. */
function Gate({ feature, children }: GateProps) {
  return useFeature(feature) ? <>{children}</> : null;
}

export const Feature = {
  Provider,
  Gate,
};
